<template>
  <div
    class="h-full"
    :data-testid="dataTestid"
    @mouseover="devices.isDesktop.value ? showProfileDropdown=true : null"
    @mouseleave="showProfileDropdown=false"
  >
    <div
      class="flex items-center h-full justify-center"
      :class="{
        // Apply background only when flag enabled; duplicate key removed
        'bg-semantic-inverse-surface-light': hasBackgroundWhenActive,
        'justify-start': iconPosition === 'left',
        'justify-center': iconPosition === 'center',
        'cursor-pointer min-w-12': iconOnly
      }"
      @click="toggleDropdown"
    >
      
      <div 
        class="rounded-full w-10 h-10 border-semantic-inverse-container-lowest border flex items-center justify-center hover:bg-semantic-inverse-container-lowest hover:text-semantic-inverse-primary"
      >
        <!-- Profile image (hidden on error or if absent) -->
        <img
          v-if="showProfilePhoto"
          class="rounded-full w-9 h-9"
          :alt="userDetails.given_name"
          :src="userDetails.picture"
          width="36"
          height="36"
          @error="onProfileImageError"
        />
        <!-- Static fallback always rendered when profile image not shown -->
        <img
          v-else
          class="rounded-full w-8 h-8 "
          alt="User"
          src="/icons/user.svg"
          width="32"
          height="32" />
      </div>

      <template v-if="!iconOnly">
        <p class="text-sm text-semantic-inverse-on-surface font-bold ml-2">
          {{ userDetails.given_name }}
        </p>

        <IconsChevron
          class="ml-2 w-4 h-4 relative top-[0.0625rem]"
          type="thin"
          :direction="showProfileDropdown ? 'top': 'bottom'"
        />
      </template>
    </div>

    <div
      v-show="showDropdown && showProfileDropdown"
      class="absolute bg-gray-10 rounded-b-md w-max top-16 shadow-md min-w-[11.5rem] z-[1]"
      :class="{ 'right-0': dropdownPosition === 'right', 'left-0': dropdownPosition === 'left' }"
      @click="handleClose"
    >
      <ul>
        <li v-for="item in menuItems" :key="item.id">
          <NuxtLink
            :id="item.id"
            class="flex items-center w-full text-semantic-surface-on-surface text-sm py-3 px-5 font-normal hover:font-bold"
            :to="item.to"
            :target="item.target || '_self'"
          >
            <img
              :width="16"
              :height="16"
              :src="item.icon"
              class="mr-2" />
            {{ item.label }}
          </NuxtLink>
        </li>

        <li>
          <nuxt-link
            id="auth_logout_btn"
            class="flex items-center w-full text-semantic-surface-on-surface text-sm py-3 px-5 font-normal hover:font-bold"
            type="button"
            @click="handleLogout"
          >
            <img
              width="16"
              height="16"
              class="mr-3"
              src="/icons/power-off-dark.svg"
              alt="Logout"
            />

            Logout
          </nuxt-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
defineOptions({
  name: 'MoleculesNavProfile'
})
const userStore = useUserStore()
const appStore = useLayoutsStore()
const router = useRouter()
const { devices } = deviceChecker()

const menuItems = ref([
  {
    id: 'dashboard_nav_btn',
    label: 'Dashboard',
    to: '/dashboard/',
    icon: '/icons/home-outline.svg'
  },
  {
    id: 'account_settings_nav_btn',
    label: 'Account Settings',
    to: '/dashboard/account-settings/',
    icon: '/icons/settings-outline.svg'
  },
  {
    id: 'support_nav_btn',
    label: 'Support',
    to: 'https://support.compareclub.com.au/en/collections/9254046-club-account-management',
    icon: '/icons/support.svg',
    target: '_blank'
  }
])

defineProps({
  dataTestid: {
    type: String,
    default: 'nav-profile'
  },
  iconOnly: {
    type: Boolean,
    default: false
  },
  dropdownPosition: {
    type: String,
    default: 'right',
    validator: value => ['right', 'left'].includes(value)
  },
  iconPosition: {
    type: String,
    default: 'center',
    validator: value => ['left', 'center'].includes(value)
  },
  hasBackgroundWhenActive: {
    type: Boolean,
    default: true
  },
  showDropdown: {
    type: Boolean,
    default: true
  }
})

/*
 * Auth User details
 * @type {ComputedRef<*>}
 */
const userDetails = computed(() => userStore.getUserProfile)

const showProfileDropdown = ref(false)
// Dual image approach: error flag + computed visibility (removes need for watcher)
const profileImageErrored = ref(false)
const showProfilePhoto = computed(() => !!userDetails.value?.picture && !profileImageErrored.value)

function onProfileImageError () {
  profileImageErrored.value = true
}

const toggleDropdown = () => {
  showProfileDropdown.value = !showProfileDropdown.value
}

// functions
const handleClose = async () => {
  await wait(200)
  showProfileDropdown.value = false
}

/*
 * Logout handler
 */
function handleLogout () {
  appStore.setPageLoader(true)
  nextTick().then(() => {
    userStore.logout()
      .finally(() => {
        sessionStorage.removeItem('is_apple_relay_email')
        router.push(generateLoginPageUrl())
        appStore.setPageLoader(false)
      })
  })
}

</script>

<style scoped>
.router-link-active {
  @apply bg-semantic-surface-container-low font-normal;
}
a:hover {
  @apply bg-semantic-surface-container-low font-normal;
}
</style>
